/**
 * Server-based returns API functions.
 * These functions call the general-system server API endpoints with authentication.
 */

import { authenticatedHttpClient } from './httpClient';
import {
  ApprovedSalesLinesResult,
  Return,
  ReturnCreate,
  ReturnUpdate,
  ReturnQueryResult,
  ReturnFilters,
} from '../models/return';

/**
 * Create a new return through the server API
 */
export const createReturnServer = async (
  organizationId: string,
  returnData: ReturnCreate,
  approvalRequired: boolean = false
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.post<Return>(
      `/returns/${organizationId}`,
      {
        return_data: returnData,
        approvalRequired: approvalRequired,
      }
    );
    return response;
  } catch (error: any) {
    console.error('Error creating return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al crear la devolución'
    );
  }
};

/**
 * Get a return by ID through the server API
 */
export const getReturnServer = async (
  organizationId: string,
  returnId: string
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.get<Return>(
      `/returns/${organizationId}/${returnId}`
    );
    return response;
  } catch (error: any) {
    console.error('Error getting return:', error);
    if (error.response?.status === 404) {
      throw new Error('Devolución no encontrada');
    }
    throw new Error(
      error.response?.data?.detail || 'Error al obtener la devolución'
    );
  }
};

/**
 * Get returns with filtering through the server API
 */
export const getReturnsServer = async (
  organizationId: string,
  filters?: {
    originalOrderId?: string;
    clientId?: string;
    clientName?: string;
    creatorSalesPerson?: string;
    approvalState?: string;
    searchTerm?: string;
    minTotal?: number;
    maxTotal?: number;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }
): Promise<ReturnQueryResult> => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await authenticatedHttpClient.get(
      `/returns/${organizationId}?${params.toString()}`
    );
    return response;
  } catch (error: any) {
    console.error('Error getting returns:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al obtener las devoluciones'
    );
  }
};

/**
 * Update a return through the server API
 */
export const updateReturnServer = async (
  organizationId: string,
  returnId: string,
  returnData: ReturnUpdate
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.put(
      `/returns/${organizationId}/${returnId}`,
      returnData
    );
    return response;
  } catch (error: any) {
    console.error('Error updating return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al actualizar la devolución'
    );
  }
};

/**
 * Delete a return through the server API
 */
export const deleteReturnServer = async (
  organizationId: string,
  returnId: string,
  softDelete: boolean = true
): Promise<void> => {
  try {
    const response = await authenticatedHttpClient.delete(
      `/returns/${organizationId}/${returnId}?soft_delete=${softDelete}`
    );
    return response;
  } catch (error: any) {
    console.error('Error deleting return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al eliminar la devolución'
    );
  }
};

/**
 * Approve a return through the server API
 */
export const approveReturnServer = async (
  organizationId: string,
  returnId: string
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/returns/${organizationId}/${returnId}/approve`
    );
    return response;
  } catch (error: any) {
    console.error('Error approving return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al aprobar la devolución'
    );
  }
};

/**
 * Reject a return through the server API
 */
export const rejectReturnServer = async (
  organizationId: string,
  returnId: string
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/returns/${organizationId}/${returnId}/reject`
    );
    return response;
  } catch (error: any) {
    console.error('Error rejecting return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al rechazar la devolución'
    );
  }
};

/**
 * Restore a soft-deleted return through the server API
 */
export const restoreReturnServer = async (
  organizationId: string,
  returnId: string
): Promise<Return> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/returns/${organizationId}/${returnId}/restore`
    );
    return response;
  } catch (error: any) {
    console.error('Error restoring return:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al restaurar la devolución'
    );
  }
};

export const getApprovedSalesLinesServer = async (
  organizationId: string,
  filters?: {
    clientId?: string;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }
): Promise<ApprovedSalesLinesResult> => {
  try {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('client_id', filters.clientId);
    if (filters?.searchTerm) params.append('search_term', filters.searchTerm);
    if (typeof filters?.limit === 'number') params.append('limit', String(filters.limit));
    if (typeof filters?.offset === 'number') params.append('offset', String(filters.offset));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return await authenticatedHttpClient.get<ApprovedSalesLinesResult>(
      `/returns/${organizationId}/approved-sales-lines${suffix}`
    );
  } catch (error: any) {
    console.error('Error fetching approved sales lines:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al obtener líneas de ventas aprobadas'
    );
  }
};

// Re-export types for convenience
export type {
  Return,
  ReturnCreate,
  ReturnUpdate,
  ReturnQueryResult,
  ReturnFilters,
};
